// The hub agent's activity as a curator reads it: agent actions, a curated
// transcript, Interactions and their Curator replies. One shape is shared by
// three parts, so each is built against the same wire: the HubAgent Durable
// Object, which projects its records into these schemas; the Exchange's
// Curator API routes, which decode what the object returns with them, so a
// field outside them never reaches a curator, except inside a transcript's
// tool-call `args` and tool-result `output`, which are open Json: only the
// object's own curation (hub-agent/src/hosted/activity.ts) keeps ranking
// internals and pi's diagnostics out of those; and Curator Studio's Agent
// activity page. An Interaction's own parts are interaction.ts's. It imports
// only effect and the Tool SDK, as the rest of the contract does. See Design
// > Hub agent, "Hosted" and "Pausing for the curator", and Design > Exchange,
// "Curator API".

import { Schema } from "effect";
import { Json, JsonObject } from "../tool-sdk/json.ts";
import type { DeliverResult } from "./hub-events.ts";
import { HubId } from "./ids.ts";
import { CuratorReply, InteractionId, InteractionState, Purpose, ReplySchema, Subject } from "./interaction.ts";

// The ID the hub agent makes for an agent action, in the hub event ID
// charset: `action:<eventId>`, so at most 7 + 300 characters. An ID is a path
// parameter of the Curator API, so its router takes a parameter of
// MAX_ACTION_ID characters (src/exchange/curator-api-handlers.ts). A brand,
// so an Interaction's ID can't go where an agent action's belongs (decided by
// Jeff on October 23, 2026; Design > Code standards, "IDs that cross a
// service are branded"). It is made only by decoding with its schema, where
// it enters: a route's params, an RPC's arguments, or the hub agent's own ID
// for a hub event.
export const MAX_ACTION_ID = 307;
export const ActionId = Schema.String.check(Schema.isPattern(new RegExp(`^[A-Za-z0-9._:-]{3,${MAX_ACTION_ID}}$`))).pipe(Schema.brand("ActionId")).annotate({ identifier: "ActionId" });
export type ActionId = typeof ActionId.Type;

/** One agent action, as a curator sees it. Cost is the hub agent's own pi-computed estimate, in US dollars. */
export const AgentActionView = Schema.Struct({
  actionId: ActionId,
  eventId: Schema.String,
  kind: Schema.String,
  model: Schema.String,
  startedAt: Schema.Int,
  finishedAt: Schema.NullOr(Schema.Int),
  status: Schema.Literals(["running", "waiting", "done", "failed", "budget_exceeded"]),
  modelCalls: Schema.Int,
  inputTokens: Schema.Int,
  outputTokens: Schema.Int,
  costUsd: Schema.Finite,
  summary: Schema.NullOr(Schema.String),
}).annotate({ identifier: "AgentActionView" });
export type AgentActionView = typeof AgentActionView.Type;

/**
 * An Interaction as a curator sees it: what it asks, how a reply must look,
 * the work it's about, how it stands, the Curator reply once there is one,
 * and its receipts. `accepted` is when the hub agent kept the reply,
 * `applied` when the reply's agent action took it up, and `done` when the
 * change it approved was carried out, which only a realization reports.
 * `approval` is an authorize Interaction's approval, until it expires. A
 * reply is its shape only: what it had to be is checkReply's, so an
 * Interaction converted from a Curator ask, whose reply was free text,
 * decodes.
 * `unread` is true for an inform the reading curator hasn't marked read,
 * in an activity read for that curator; it is false for every other
 * purpose, and in every other answer, such as a reply's. See Design > Hub
 * agent, "Pausing for the curator".
 */
export const InteractionView = Schema.Struct({
  interactionId: InteractionId,
  actionId: ActionId,
  at: Schema.Int,
  purpose: Purpose,
  prompt: Schema.String,
  replySchema: Schema.NullOr(ReplySchema),
  subject: Schema.NullOr(Subject),
  state: InteractionState,
  expiresAt: Schema.NullOr(Schema.Int),
  closedAt: Schema.NullOr(Schema.Int),
  supersededBy: Schema.NullOr(InteractionId),
  reply: Schema.NullOr(Schema.Struct({ ...CuratorReply.fields, at: Schema.Int })),
  receipts: Schema.Struct({
    accepted: Schema.NullOr(Schema.Int),
    applied: Schema.NullOr(Schema.Int),
    done: Schema.NullOr(Schema.Struct({ at: Schema.Int, ok: Schema.Boolean, detail: Schema.String.check(Schema.isMaxLength(1000)) })),
  }),
  approval: Schema.NullOr(Schema.Struct({ until: Schema.Int })),
  unread: Schema.Boolean,
}).annotate({ identifier: "Interaction" });
export type InteractionView = typeof InteractionView.Type;

/**
 * One item of an agent action's transcript, curated: what the agent said, the
 * tools it called and what they returned, the Interactions it opened and the
 * Curator replies to them. A model response the runtime cut off, such as by
 * a restart mid-stream, is kept with `aborted`: its tool calls never ran
 * (warmhub/hub#595 at a814d54d, results.md). Platform instructions, playbook text,
 * gateway metadata and retry internals are never items (decision
 * what-may-a-curator-see-of-an-agent, 2026-10-23). A tool result is the JSON
 * the tool returned, with event ranking internals removed (warmhub/hub#454),
 * cut to 8,000 characters.
 */
export const TranscriptItem = Schema.Union([
  Schema.Struct({ type: Schema.Literal("agent"), at: Schema.Int, text: Schema.String, aborted: Schema.optionalKey(Schema.Literal(true)) }),
  Schema.Struct({ type: Schema.Literal("tool-call"), at: Schema.Int, callId: Schema.String, tool: Schema.String, args: Json, aborted: Schema.optionalKey(Schema.Literal(true)) }),
  Schema.Struct({ type: Schema.Literal("tool-result"), at: Schema.Int, callId: Schema.String, tool: Schema.String, ok: Schema.Boolean, output: Json, truncated: Schema.Boolean }),
  Schema.Struct({ type: Schema.Literal("interaction"), at: Schema.Int, interactionId: InteractionId }),
  Schema.Struct({ type: Schema.Literal("reply"), at: Schema.Int, interactionId: InteractionId, reply: CuratorReply }),
]).annotate({ identifier: "TranscriptItem" });
export type TranscriptItem = typeof TranscriptItem.Type;

/**
 * The hub agent's tools, by the names a transcript shows a curator in its
 * tool-call and tool-result items. The hub agent defines one tool per name
 * (hub-agent/src/hosted/tools.ts); Studio words them for a curator.
 */
export const HUB_AGENT_TOOL_NAMES = ["readHubStatus", "listToolsets", "readTestRun", "readRecentAgentActions", "openInteraction", "readHubSummary", "readDemand", "readHubEvents", "saveIntents", "saveShapes", "saveGolden", "saveAudit", "write", "edit", "runTests", "read", "grep", "sampleThings", "findThings"] as const;
export type HubAgentToolName = (typeof HUB_AGENT_TOOL_NAMES)[number];

/**
 * How many agent actions, and closed Interactions, an activity read takes:
 * GET /api/v1/hubs/:hub/agent's ?limit, and HubAgentCuratorRpc.activity's
 * limit, which the object decodes with ActivityLimit, taking the default for
 * one that doesn't decode.
 */
export const ACTIVITY_LIMIT = { minimum: 1, maximum: 100, byDefault: 50 } as const;
export const ActivityLimit = Schema.Int.check(Schema.isBetween({ minimum: ACTIVITY_LIMIT.minimum, maximum: ACTIVITY_LIMIT.maximum }));

/**
 * GET /api/v1/hubs/:hub/agent: the hub's newest agent actions, and its
 * Interactions, every open one, the newest closed, and up to
 * UNREAD_LIMIT informs unread by the caller, however old, each list newest
 * first; and `pausedUntil`, the UTC midnight at which its agent actions
 * resume while a daily spend limit pauses them, or null. It doesn't say which
 * limit: a curator isn't told of other hubs.
 */
export const AgentActivityResponse = Schema.Struct({
  actions: Schema.Array(AgentActionView),
  interactions: Schema.Array(InteractionView),
  pausedUntil: Schema.NullOr(Schema.Int),
}).annotate({ identifier: "AgentActivityResponse" });
export type AgentActivityResponse = typeof AgentActivityResponse.Type;

/** One Interaction that waits on the curator, with the Hub ID of the hub whose hub agent opened it. */
export const WaitingInteraction = Schema.Struct({ hubId: HubId, interaction: InteractionView }).annotate({ identifier: "WaitingInteraction" });
export type WaitingInteraction = typeof WaitingInteraction.Type;

/**
 * GET /api/v1/agent/interactions: every Interaction waiting on the curator,
 * open and not an inform, on the hubs the caller curates, oldest first;
 * `unread`, the informs the caller hasn't marked read there, up to
 * UNREAD_LIMIT a hub, newest first; and the Hub IDs of those hubs whose hub
 * agent couldn't be read this time, whose Interactions are in neither list.
 * Where the Exchange binds no hub agent, all three are empty. See Design >
 * Exchange, "The hub agent's activity".
 */
export const WaitingInteractionsResponse = Schema.Struct({
  interactions: Schema.Array(WaitingInteraction),
  unread: Schema.Array(WaitingInteraction),
  unavailable: Schema.Array(HubId),
}).annotate({ identifier: "WaitingInteractionsResponse" });
export type WaitingInteractionsResponse = typeof WaitingInteractionsResponse.Type;

/** GET /api/v1/hubs/:hub/agent/actions/:actionId: one agent action and its curated transcript, oldest item first. */
export const AgentActionTranscript = Schema.Struct({
  action: AgentActionView,
  items: Schema.Array(TranscriptItem),
}).annotate({ identifier: "AgentActionTranscript" });
export type AgentActionTranscript = typeof AgentActionTranscript.Type;

/**
 * One conversation of an agent action's trajectory: its ID, the
 * conversation that owns it, or null for the agent action's own, the build
 * stage and a Tools lane's intent its owning task names, or null, and the
 * agent action's share of its entries, oldest first, each as pi keeps it
 * (EntryRecord): its kind, its model messages with their tool calls, tool
 * results, usage and timestamps, and its data. A provider's error text and
 * pi's diagnostics are left out (Design > Errors, "Redaction").
 */
export const TrajectoryConversation = Schema.Struct({
  conversationId: Schema.Int,
  ownerConversationId: Schema.NullOr(Schema.Int),
  stage: Schema.NullOr(Schema.String),
  intent: Schema.NullOr(Schema.String),
  entries: Schema.Array(JsonObject),
}).annotate({ identifier: "TrajectoryConversation" });
export type TrajectoryConversation = typeof TrajectoryConversation.Type;

/**
 * GET /api/v1/hubs/:hub/agent/actions/:actionId/trajectory, the operator's
 * alone: one agent action and its trajectory, every conversation of its
 * tree that it ran, its own first, then each build stage's and Tools lane's,
 * whole, for an improvement loop to read. See Design > Hub agent,
 * "Reading a build's trajectory".
 */
export const AgentTrajectory = Schema.Struct({
  action: AgentActionView,
  conversations: Schema.Array(TrajectoryConversation),
}).annotate({ identifier: "AgentTrajectory" });
export type AgentTrajectory = typeof AgentTrajectory.Type;

/**
 * POST /api/v1/hubs/:hub/agent/interactions/:interactionId/reply's success,
 * 200: the Interaction, with the Curator reply it kept, which the hub agent
 * has taken up.
 */
export const CuratorReplyResponse = Schema.Struct({ delivery: Schema.Literal("delivered"), interaction: InteractionView }).annotate({ identifier: "CuratorReplyResponse" });
export type CuratorReplyResponse = typeof CuratorReplyResponse.Type;

/**
 * The reply route's other success, 202: the reply is kept on the
 * Interaction, but the hub agent couldn't take it up yet. It does on its next
 * alarm, without another request. `detail` is the Exchange's text for the
 * curator.
 */
export const CuratorReplyPending = Schema.Struct({ delivery: Schema.Literal("pending"), interaction: InteractionView, detail: Schema.String }).annotate({ identifier: "CuratorReplyPending" });
export type CuratorReplyPending = typeof CuratorReplyPending.Type;

// What each HubAgentCuratorRpc method returns: one schema per method, which
// types the method here and which the Exchange decodes its result with, so
// the object's returns and the Exchange's decode are held to one wire.
const Unavailable = Schema.Struct({ result: Schema.Literal("unavailable"), detail: Schema.String });
const NotFoundResult = Schema.Struct({ result: Schema.Literal("not_found") });
export const ActivityResult = Schema.Union([Schema.Struct({ result: Schema.Literal("ok"), activity: AgentActivityResponse }), Unavailable]);
export type ActivityResult = typeof ActivityResult.Type;
export const TranscriptResult = Schema.Union([Schema.Struct({ result: Schema.Literal("ok"), transcript: AgentActionTranscript }), NotFoundResult, Unavailable]);
export type TranscriptResult = typeof TranscriptResult.Type;
export const TrajectoryResult = Schema.Union([Schema.Struct({ result: Schema.Literal("ok"), trajectory: AgentTrajectory }), NotFoundResult, Unavailable]);
export type TrajectoryResult = typeof TrajectoryResult.Type;
export const ReplyResult = Schema.Union([
  Schema.Struct({ result: Schema.Literal("ok"), interaction: InteractionView }), Schema.Struct({ result: Schema.Literal("stored_pending"), interaction: InteractionView }),
  Schema.Struct({ result: Schema.Literal("invalid") }), Schema.Struct({ result: Schema.Literal("reply_invalid"), detail: Schema.String }), NotFoundResult,
  Schema.Struct({ result: Schema.Literal("closed"), interaction: InteractionView }), Unavailable,
]);
export type ReplyResult = typeof ReplyResult.Type;
export const MarkReadResult = Schema.Union([Schema.Struct({ result: Schema.Literal("ok") }), Schema.Struct({ result: Schema.Literal("invalid") }), Unavailable]);
export type MarkReadResult = typeof MarkReadResult.Type;
export const RequeueResult = Schema.Union([
  Schema.Struct({ result: Schema.Literals(["accepted", "duplicate"]), eventId: Schema.String }), NotFoundResult,
  Schema.Struct({ result: Schema.Literal("refused"), detail: Schema.String }), Unavailable,
]);
export type RequeueResult = typeof RequeueResult.Type;

/**
 * POST /api/v1/hubs/:hub/agent/events/:eventId/requeue's success, 202: the
 * hub event's new eventId (requeuedEventId), and whether the Hub inbox kept
 * it or already had it. Operator only. See Design > Operations, "Requeuing a
 * hub event".
 */
export const RequeueResponse = Schema.Struct({ eventId: Schema.String, result: Schema.Literals(["accepted", "duplicate"]) }).annotate({ identifier: "RequeueResponse" });
export type RequeueResponse = typeof RequeueResponse.Type;

/**
 * POST /api/v1/hubs/:hub/toolsets/:commit/repair's success, 202: the
 * curator.repair.requested hub event the request became, and whether the Hub inbox
 * kept it or already had it. The hub agent answers it later, with an
 * Interaction. See Design > Hub agent, "Repair rounds".
 */
export const RequestRepairResponse = Schema.Struct({ eventId: Schema.String, result: Schema.Literals(["accepted", "duplicate"]) }).annotate({ identifier: "RequestRepairResponse" });
export type RequestRepairResponse = typeof RequestRepairResponse.Type;

/** How many unread informs a hub's activity read returns, whatever its limit, and how many one read mark request names: 50. */
export const UNREAD_LIMIT = 50;

/** POST /api/v1/hubs/:hub/agent/interactions/read: the informs the caller has read, up to UNREAD_LIMIT. */
export const MarkInteractionsRead = Schema.Struct({
  interactionIds: Schema.Array(InteractionId).check(Schema.isMaxLength(UNREAD_LIMIT)),
}).annotate({ identifier: "MarkInteractionsRead" });
export type MarkInteractionsRead = typeof MarkInteractionsRead.Type;

/**
 * What the Exchange calls on a hub's HubAgent Durable Object, by its Hub ID
 * through a script_name binding. Each method returns the contract's wire
 * shape; the Exchange decodes it again before a curator sees it.
 * - An Interaction takes one Curator reply. The same reply again is `ok`, and
 *   a different one, or any reply once it's closed, is `closed`, with the
 *   Interaction as it is.
 * - A reply whose arguments don't decode, such as an ID outside
 *   InteractionId, is `invalid`, and keeps nothing.
 * - A reply its Interaction's reply schema refuses is `reply_invalid`, with
 *   checkReply's words, and keeps nothing.
 * - A reply kept whose hub event the object couldn't take up is
 *   `stored_pending`: the object takes it up on its next alarm.
 * - `by` is the WarmHub user ID of the curator who replied, which an
 *   approval names.
 * - `userId` in `activity()` is the reading curator's, whose read marks set
 *   each inform's `unread`; null reads every inform as read. `markRead()`
 *   keeps a read mark for `userId` on each named inform, once, and ignores
 *   an ID that isn't an inform of the hub's.
 * - `requeue()` delivers the Hub inbox row `eventId` again, its kind and ref
 *   under requeuedEventId(eventId, at), as `deliver()` would: `accepted` or
 *   `duplicate` with that eventId, `not_found` when the inbox has no such
 *   row, or `refused`. The operator's, never a curator's.
 * - `trajectory()` reads one agent action's trajectory (AgentTrajectory):
 *   `ok`, `not_found` when the object has no such agent action, or
 *   `unavailable`. The operator's, never a curator's.
 * Kept by the Exchange's curator check, never by the object: the object
 * trusts its caller, which only the Exchange is. The Exchange's producers of
 * hub events call `deliver()` on the same object, which keeps a hub event in
 * its Hub inbox once per eventId (src/contract/hub-events.ts).
 */
export interface HubAgentCuratorRpc {
  deliver(event: Json, requestId: string | null): Promise<DeliverResult>;
  activity(limit: number, userId: string | null, requestId: string | null): Promise<ActivityResult>;
  transcript(actionId: ActionId, requestId: string | null): Promise<TranscriptResult>;
  reply(interactionId: InteractionId, reply: CuratorReply, by: string | null, at: number, requestId: string | null): Promise<ReplyResult>;
  markRead(interactionIds: readonly InteractionId[], userId: string, at: number, requestId: string | null): Promise<MarkReadResult>;
  requeue(eventId: string, at: number, requestId: string | null): Promise<RequeueResult>;
  trajectory(actionId: ActionId, requestId: string | null): Promise<TrajectoryResult>;
}
